import { forwardRef } from 'react';
import type { FontValue } from '@elfsight/configurator-contracts';
import type {
  TimerDefaultSettingsProps,
  TimerProps
} from '@elfsight/widget-ui';
import { Timer, useTimerValue } from '@elfsight/widget-ui';
import { getInvertedColor } from '@elfsight/widget-utils';
import type { BlockProps } from '../_block';
import { Block } from '../_block';
import { timerSettings } from './timerSettings';

export type TimerBlockProps = BlockProps &
  TimerProps &
  TimerDefaultSettingsProps & {
    applyMobileSettings: boolean;
    daysLabel: string;
    hoursLabel: string;
    minutesLabel: string;
    secondsLabel: string;
    fontFamily: FontValue;
    sizeOnMobile: TimerProps['size'];
  };

export const TimerBlock = forwardRef<HTMLDivElement, TimerBlockProps>(
  (
    {
      alignment,
      editMode,
      widgetId,
      style,
      applyMobileSettings,
      timerStyle = timerSettings.timerStyle,
      timerType,
      remainingRestartUnit,
      remainingRestartValue,
      repeatTimerEnabled,
      timerDuration,
      durationUnit,
      daysLabel,
      hoursLabel,
      minutesLabel,
      secondsLabel,
      fontFamily,
      size,
      sizeOnMobile,
      timerColor = timerSettings.timerColor,
      ...forwardingProps
    },
    forwardingRef
  ) => {
    const timerValue = useTimerValue({
      editMode,
      widgetId,
      timerType,
      timerDuration,
      durationUnit,
      remainingRestartUnit,
      remainingRestartValue,
      repeatTimerEnabled,
      ...forwardingProps
    });

    const timerStylesWithBackground = ['separated', 'combined', 'gradient'];

    const numberColor = timerStylesWithBackground.includes(timerStyle)
      ? getInvertedColor(timerColor)
      : timerColor;

    return (
      <Block
        ref={forwardingRef}
        alignment={alignment}
        style={{ ...style, fontFamily: fontFamily?.value }}
      >
        <Timer
          {...forwardingProps}
          {...timerValue}
          timerStyle={timerStyle}
          timerBackgroundColor={timerColor}
          timerColor={numberColor}
          separatorColor={timerColor}
          unitLabelColor={timerColor}
          unitLabelTexts={{
            days: daysLabel,
            hours: hoursLabel,
            minutes: minutesLabel,
            seconds: secondsLabel
          }}
          size={applyMobileSettings ? sizeOnMobile : size}
        />
      </Block>
    );
  }
);
